import React, { useRef, useState } from 'react';

interface SelectedFile {
  dataUrl: string;
  type: string;
  name: string;
}

interface ChatInputProps {
  disabled: boolean;
  onSend: (text: string, file?: { dataUrl: string; type: string; name: string }) => void;
  // Rendered at the top of the input box (the patient focus pill).
  header?: React.ReactNode;
}

// Mirror the backend gate: images and PDFs only, max 10 MB (base64 encoding
// happens server-side, so we measure the raw file size here).
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const isAllowedType = (type: string): boolean =>
  type.startsWith('image/') || type === 'application/pdf';

// FileReader.readAsDataURL yields "data:<mime>;base64,…" — exactly the inlined
// data URL the socket expects as file_data.
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const ChatInput: React.FC<ChatInputProps> = ({ disabled, onSend, header }) => {
  const [text, setText] = useState('');
  const [file, setFile] = useState<SelectedFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Nested elements fire dragenter/dragleave as the pointer moves within the
  // drop zone; counting enters vs. leaves keeps the highlight from flickering.
  const dragDepthRef = useRef(0);

  const canSend = !disabled && (text.trim() !== '' || file !== null);
  const isPdf = file?.type === 'application/pdf';

  const submit = () => {
    if (!canSend) return;
    onSend(text.trim(), file ? { dataUrl: file.dataUrl, type: file.type, name: file.name } : undefined);
    setText('');
    setFile(null);
    setError(null);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const acceptFile = async (picked: File) => {
    if (!isAllowedType(picked.type)) {
      setError('Solo se admiten imágenes y archivos PDF.');
      return;
    }
    if (picked.size > MAX_FILE_BYTES) {
      setError('El archivo es demasiado grande. Tamaño máximo: 10 MB.');
      return;
    }
    setError(null);
    try {
      const dataUrl = await readFileAsDataUrl(picked);
      setFile({ dataUrl, type: picked.type, name: picked.name });
    } catch (err) {
      console.warn('ChatInput: failed to read file', err);
      setError('No se pudo leer el archivo.');
    }
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    e.target.value = '';
    if (picked) acceptFile(picked);
  };

  const onDragEnter = (e: React.DragEvent) => {
    if (disabled || !e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragActive(true);
  };

  const onDragOver = (e: React.DragEvent) => {
    if (disabled || !e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDragLeave = (e: React.DragEvent) => {
    if (disabled) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  };

  const onDrop = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragActive(false);
    // Only the first file — the composer attaches one at a time.
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) acceptFile(dropped);
  };

  return (
    <div
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '12px 12px 20px',
        borderTop: '1px solid var(--border)',
        background: 'var(--card)',
      }}
    >
      {dragActive && (
        <div
          style={{
            position: 'absolute',
            inset: 4,
            zIndex: 5,
            borderRadius: 12,
            border: '2px dashed var(--primary)',
            background: 'rgba(56,178,172,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--primary)',
            pointerEvents: 'none',
          }}
        >
          Suelta una imagen o PDF
        </div>
      )}

      {header}

      {file && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative' }}>
            {isPdf ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  maxWidth: 220,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--muted)',
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 20 }}>📄</span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--foreground)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={file.name}
                >
                  {file.name}
                </span>
              </div>
            ) : (
              <img
                src={file.dataUrl}
                alt={file.name}
                style={{
                  width: 56,
                  height: 56,
                  objectFit: 'cover',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                }}
              />
            )}
            <button
              onClick={() => setFile(null)}
              aria-label="Quitar archivo"
              style={{
                position: 'absolute',
                top: -6,
                right: -6,
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: 'none',
                background: 'var(--foreground)',
                color: 'var(--card)',
                fontSize: 12,
                lineHeight: 1,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {error && (
        <div style={{ fontSize: 12, color: 'var(--destructive)' }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          onChange={onPickFile}
          style={{ display: 'none' }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          aria-label="Adjuntar archivo"
          title="Adjuntar imagen o PDF"
          style={{
            border: '1px solid var(--border)',
            borderRadius: 10,
            width: 38,
            height: 38,
            flexShrink: 0,
            background: 'var(--card)',
            color: disabled ? 'var(--muted-foreground)' : 'var(--primary)',
            fontSize: 18,
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          📎
        </button>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          placeholder="Escribe al asistente…"
          rows={1}
          style={{
            flex: 1,
            resize: 'none',
            maxHeight: 120,
            minHeight: 38,
            padding: '9px 12px',
            borderRadius: 10,
            border: '1px solid var(--border)',
            fontFamily: 'inherit',
            fontSize: 14,
            lineHeight: 1.4,
            outline: 'none',
            color: disabled ? 'var(--muted-foreground)' : 'var(--foreground)',
            background: disabled ? 'var(--border)' : 'var(--card)',
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
        <button
          onClick={submit}
          disabled={!canSend}
          style={{
            border: 'none',
            borderRadius: 10,
            padding: '9px 16px',
            height: 38,
            background: canSend ? 'var(--primary)' : 'var(--primary-subtle)',
            color: 'var(--card)',
            fontWeight: 600,
            fontSize: 14,
            cursor: canSend ? 'pointer' : 'default',
          }}
        >
          Enviar
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
